import { makeAutoObservable, runInAction } from "mobx";
import { OsaNodeApi } from "../services/OsaNodeApi";
import { RootStore } from "../../shared/stores/RootStore";
import { OsaNode } from "../models/OsaNode";
import { OptionalScope } from "../../shared/models/OptionalScope";
import { OsaWayApi } from "../services/OsaWayApi";
import { MapboxApi } from "../../map/services/MapboxApi";

export const defaultNodeScopes = [
  OptionalScope.Score,
  OptionalScope.SafetyTreatmentModel,
  OptionalScope.Photo,
  OptionalScope.ScoreImpact,
  OptionalScope.CodingSuggestions,
];

interface NodeReference {
  osaWayId: string;
  index: number;
}

type osaWayId = string;
type osaNodeId = string;

export const nodeReferences = new Map<string, NodeReference>();

export class OsaNodeStore {
  rootStore;
  nodesByWayId = new Map<osaWayId, OsaNode[]>();
  selectedId?: osaNodeId;
  multiSelectedIds = new Set<osaNodeId>();

  private nodeRefreshTimeout?: NodeJS.Timeout;
  showNodeRefreshAlert = false;

  /** data collection stage */
  pendingCoordsByNodeId = new Map<osaNodeId, number[]>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.nodesByWayId = new Map();
      this.selectedId = undefined;

      this.showNodeRefreshAlert = false;
      if (this.nodeRefreshTimeout) clearInterval(this.nodeRefreshTimeout);
      this.nodeRefreshTimeout = undefined;
    });
  }

  private getFrom(nodeId: string, source: Map<string, OsaNode[]>) {
    const reference = nodeReferences.get(nodeId);
    if (reference) {
      return source.get(reference.osaWayId)?.[reference.index];
    }
    return;
  }

  /** will get the forecasted version if available */
  get(nodeId: string) {
    const { filterStore } = this.rootStore;
    return this.getFrom(nodeId, filterStore.originalOrForecastedNodes);
  }

  /** will always get the non-forecasted version */
  getOriginal(nodeId: string) {
    const { filterStore } = this.rootStore;
    return this.getFrom(nodeId, filterStore.nodesAvailableInCurrentMode);
  }

  getSelected() {
    if (!this.selectedId) return;
    return this.get(this.selectedId);
  }

  select(nodeId: string) {
    runInAction(() => (this.selectedId = nodeId));

    this.updateOsaMapSource();
  }

  setMultiSelectedIds(value: Set<string>) {
    runInAction(() => (this.multiSelectedIds = value));

    this.updateOsaMapSource();
  }

  updateOsaMapSource() {
    const { mapStore } = this.rootStore;
    mapStore.osaMapSource?.onStoreUpdate();
  }

  toggleMultiSelectedId(nodeId: string) {
    const newSet = new Set(this.multiSelectedIds);
    if (newSet.has(nodeId)) {
      newSet.delete(nodeId);
    } else {
      newSet.add(nodeId);
    }
    this.setMultiSelectedIds(newSet);
  }

  getMultiSelectedNodesByOsaWay(osaWayId: string): OsaNode[] {
    const nodes: OsaNode[] = [];
    for (const nodeId of this.multiSelectedIds) {
      const node = this.get(nodeId);
      if (node?.osaWayId === osaWayId) {
        nodes.push(node);
      }
    }
    return nodes;
  }

  async matchRoute(nodes: OsaNode[]) {
    const chunkSize = 100;

    for (let i = 0; i < nodes.length; i += chunkSize) {
      const nodesChunk = nodes.slice(i, i + chunkSize);
      const coordString = nodesChunk
        .map((node) => `${node.longitude},${node.latitude}`)
        .join(";");

      await MapboxApi.mapMatching(coordString).then((tracepoints) => {
        tracepoints.forEach((tracepoint, index) => {
          if (tracepoint) {
            const matchedNode = nodesChunk[index];
            this.pendingCoordsByNodeId.set(matchedNode.id, tracepoint.location);
          }
        });
      });
    }

    runInAction(() => {
      this.pendingCoordsByNodeId = new Map(this.pendingCoordsByNodeId);
    });
    this.updatePendingCoordChangesMapSource();
  }

  setNodeCoords(nodeId: string, lat: number, lng: number) {
    runInAction(() => {
      this.pendingCoordsByNodeId.set(nodeId, [lng, lat]);
      this.pendingCoordsByNodeId = new Map(this.pendingCoordsByNodeId);
    });

    this.updateOsaMapSource();
    this.updatePendingCoordChangesMapSource();
  }

  updatePendingCoordChangesMapSource() {
    const { mapStore } = this.rootStore;
    mapStore.pendingCoordChangesMapSource?.onStoreUpdate();
  }

  clearPendingCoordChanges() {
    runInAction(() => {
      this.pendingCoordsByNodeId = new Map();
    });

    this.updateOsaMapSource();
    this.updatePendingCoordChangesMapSource();
  }

  async applyPendingCoordChanges(osaWayId: string) {
    await OsaWayApi.batchUpdateLocation(
      osaWayId,
      [...this.pendingCoordsByNodeId].map(([nodeId, coords]) => ({
        osaNodeId: nodeId,
        longitude: coords[0],
        latitude: coords[1],
      })),
    );
  }

  clearSelected() {
    runInAction(() => {
      this.selectedId = undefined;
      this.multiSelectedIds = new Set();
    });
  }

  async deleteSelectedNodes(onDelete?: (nodeId: string) => void) {
    const newNodesByWayId = new Map(this.nodesByWayId);

    for (const nodeId of this.multiSelectedIds.values()) {
      await OsaNodeApi.delete(nodeId);
      onDelete?.(nodeId);

      const reference = nodeReferences.get(nodeId)!;
      newNodesByWayId.set(
        reference.osaWayId,
        newNodesByWayId
          .get(reference.osaWayId)!
          .filter((node) => node.id !== nodeId),
      );
    }

    runInAction(() => {
      this.nodesByWayId = newNodesByWayId;

      // select the node that replaces the selectedId in the route's order
      if (this.selectedId && this.multiSelectedIds.has(this.selectedId)) {
        const previouslySelectedReference = nodeReferences.get(
          this.selectedId,
        )!;
        const newNodeSelection = newNodesByWayId.get(
          previouslySelectedReference.osaWayId,
        )![previouslySelectedReference.index];
        if (newNodeSelection) {
          this.select(newNodeSelection.id);
        }
      }
      this.rebuildNodeReferences();
      this.multiSelectedIds = new Set();
    });
  }

  rebuildNodeReferences() {
    for (const [osaWayId, nodes] of this.nodesByWayId) {
      nodes.forEach((node, index) => {
        nodeReferences.set(node.id, { index, osaWayId });
      });
    }
  }

  async fetchNodes(
    osaWayId: string,
    scopes: OptionalScope[] = defaultNodeScopes,
    cancelIf?: () => boolean,
  ) {
    await OsaWayApi.compileAllNodePages(osaWayId, scopes, 400).then((nodes) => {
      if (cancelIf?.()) return;

      runInAction(() => {
        this.nodesByWayId.set(osaWayId, nodes);
        this.nodesByWayId = new Map(this.nodesByWayId);
      });
      nodes.forEach((node, index) => {
        nodeReferences.set(node.id, { osaWayId, index });
      });
    });

    this.resetNodeRefreshTimeout();
  }

  async refreshNodeScore(nodeId: string) {
    const node = this.get(nodeId);
    if (node) {
      const response = await OsaNodeApi.getScore(node.id!);

      runInAction(() => {
        node!.score = response.data || null;
        this.nodesByWayId = new Map(this.nodesByWayId);
      });
    }
  }

  private resetNodeRefreshTimeout = () => {
    const callback = () => {
      runInAction(() => {
        this.showNodeRefreshAlert = true;
        this.nodeRefreshTimeout = undefined;
      });
    };
    runInAction(() => {
      this.showNodeRefreshAlert = false;
      if (this.nodeRefreshTimeout) clearInterval(this.nodeRefreshTimeout);
      const ms = 1000 * 60 * 60; // 1 hour
      this.nodeRefreshTimeout = setTimeout(callback, ms);
    });
  };

  closeNodeRefreshAlert = () => {
    runInAction(() => {
      this.showNodeRefreshAlert = false;
    });
  };
}
